<template>
  <div class="page-container">
    <div class="bg-grid"></div>
    <div class="bg-glow"></div>

    <header class="page-header">
      <div class="breadcrumb">
        <router-link to="/" class="breadcrumb-link">{{ $i18n.locale === 'zh' ? '首页' : 'Home' }}</router-link>
        <span class="breadcrumb-sep">/</span>
        <span class="breadcrumb-current">{{ $i18n.t('pages.json.title') }}</span>
      </div>
      <div class="header-content">
        <div class="header-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <polyline points="16 18 22 12 16 6"/>
            <polyline points="8 6 2 12 8 18"/>
            <line x1="14" y1="4" x2="10" y2="20"/>
          </svg>
        </div>
        <h1 class="page-title">{{ $i18n.t('pages.json.title') }}</h1>
        <p class="page-subtitle">{{ $i18n.t('pages.json.subtitle') }}</p>
      </div>
    </header>

    <main class="page-content">
      <div class="converter-card">
        <div class="converter-grid">
          <div class="panel input-panel">
            <div class="panel-header">
              <label class="panel-label">{{ $i18n.t('pages.json.inputLabel') }}</label>
              <div class="panel-actions">
                <button @click="formatJSON" class="action-btn">{{ $i18n.t('pages.json.format') }}</button>
                <button @click="minifyJSON" class="action-btn">{{ $i18n.t('pages.json.minify') }}</button>
                <button @click="clearInput" class="action-btn">{{ $i18n.t('pages.json.clear') }}</button>
              </div>
            </div>
            <textarea
              v-model="inputJSON"
              :placeholder="$i18n.locale === 'zh' ? '粘贴 JSON 数据...' : 'Paste JSON data...'"
              class="json-textarea"
              rows="18"
            ></textarea>
            <div v-if="parseError" class="error-msg">{{ parseError }}</div>
          </div>

          <div class="panel output-panel">
            <div class="panel-header">
              <label class="panel-label">{{ $i18n.t('pages.json.outputLabel') }}</label>
              <div class="panel-actions">
                <button @click="copyOutput" class="action-btn" :disabled="!outputJSON">{{ $i18n.t('pages.json.copy') }}</button>
                <button @click="downloadJSON" class="action-btn" :disabled="!outputJSON">{{ $i18n.t('pages.json.download') }}</button>
              </div>
            </div>
            <pre v-if="outputJSON" class="json-output">{{ outputJSON }}</pre>
            <div v-else class="output-placeholder">
              <p>{{ $i18n.t('pages.json.placeholder') }}</p>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'JSONConverterPage',
  inject: ['i18n'],
  data() {
    return {
      inputJSON: '',
      outputJSON: '',
      parseError: ''
    }
  },
  methods: {
    formatJSON() {
      if (!this.inputJSON.trim()) {
        return
      }
      try {
        const parsed = JSON.parse(this.inputJSON)
        this.outputJSON = JSON.stringify(parsed, null, 2)
        this.parseError = ''
      } catch (e) {
        this.parseError = (this.$i18n.locale === 'zh' ? 'JSON 格式错误: ' : 'JSON format error: ') + e.message
        this.outputJSON = ''
      }
    },
    minifyJSON() {
      if (!this.inputJSON.trim()) {
        return
      }
      try {
        const parsed = JSON.parse(this.inputJSON)
        this.outputJSON = JSON.stringify(parsed)
        this.parseError = ''
      } catch (e) {
        this.parseError = (this.$i18n.locale === 'zh' ? 'JSON 格式错误: ' : 'JSON format error: ') + e.message
        this.outputJSON = ''
      }
    },
    clearInput() {
      this.inputJSON = ''
      this.outputJSON = ''
      this.parseError = ''
    },
    async copyOutput() {
      if (!this.outputJSON) {
        return
      }
      try {
        await navigator.clipboard.writeText(this.outputJSON)
      } catch (e) {
        console.error('Copy failed', e)
      }
    },
    downloadJSON() {
      if (!this.outputJSON) {
        return
      }
      const blob = new Blob([this.outputJSON], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `json-${Date.now()}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  padding: 40px 24px 60px;
  position: relative;
  overflow: hidden;
}

.bg-grid {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    linear-gradient(rgba(232, 255, 0, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(232, 255, 0, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
}

.bg-glow {
  position: fixed;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(0, 240, 255, 0.1) 0%, transparent 70%);
  top: 50%;
  right: -200px;
  transform: translateY(-50%);
  filter: blur(100px);
  pointer-events: none;
  z-index: 0;
}

.page-header {
  max-width: 1000px;
  margin: 0 auto 50px;
  position: relative;
  z-index: 1;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 30px;
  font-family: var(--font-mono);
  font-size: 12px;
}

.breadcrumb-link {
  color: var(--text-secondary);
  text-decoration: none;
  transition: color 0.3s;
}

.breadcrumb-link:hover {
  color: var(--accent-primary);
}

.breadcrumb-sep {
  color: var(--border-color);
}

.breadcrumb-current {
  color: var(--accent-success);
}

.header-content {
  text-align: center;
}

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  color: var(--accent-success);
  margin-bottom: 20px;
}

.page-title {
  font-family: var(--font-display);
  font-size: 48px;
  letter-spacing: 2px;
  color: var(--text-primary);
  margin-bottom: 12px;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  font-weight: 300;
}

.page-content {
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.converter-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 32px;
}

.converter-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.panel {
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.panel-label {
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.panel-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  padding: 6px 12px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  color: var(--text-secondary);
  font-family: var(--font-body);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-btn:hover:not(:disabled) {
  border-color: var(--accent-success);
  color: var(--accent-success);
}

.action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.json-textarea {
  flex: 1;
  width: 100%;
  padding: 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  color: var(--text-primary);
  font-family: var(--font-mono);
  font-size: 13px;
  line-height: 1.6;
  resize: vertical;
  min-height: 300px;
  transition: border-color 0.3s;
}

.json-textarea::placeholder {
  color: var(--text-secondary);
}

.json-textarea:focus {
  outline: none;
  border-color: var(--accent-success);
}

.error-msg {
  margin-top: 12px;
  padding: 12px 16px;
  background: rgba(255, 61, 0, 0.1);
  border: 1px solid rgba(255, 61, 0, 0.3);
  border-radius: 8px;
  color: var(--accent-warm);
  font-size: 13px;
}

.json-output {
  flex: 1;
  width: 100%;
  min-height: 300px;
  padding: 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  color: var(--accent-success);
  font-family: var(--font-mono);
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-y: auto;
  box-sizing: border-box;
}

.output-placeholder {
  flex: 1;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-secondary);
  border: 1px dashed var(--border-color);
  border-radius: 12px;
}

.output-placeholder p {
  color: var(--text-secondary);
  font-size: 14px;
}

@media (max-width: 768px) {
  .page-container {
    padding: 24px 16px 40px;
  }

  .page-title {
    font-size: 36px;
  }

  .converter-card {
    padding: 20px;
  }

  .converter-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .json-textarea,
  .json-output {
    min-height: 200px;
  }
}
</style>
